"use client"

import { useState } from "react"
import { Check, XCircle, Loader2 } from "lucide-react"
import MODAL_NAMES from "../../../dialogs/ModalName"
import { hideModalFromId } from "../../../../shared/functions/ModalShow"


interface AdminActionModalProps {
  adminAction: "approve" | "reject" | null
  onConfirm: (comment: string, action: "approve" | "reject") => Promise<void>
}

export const AdminActionModal = ({ adminAction, onConfirm }: AdminActionModalProps) => {
  const [adminComment, setAdminComment] = useState("")
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    if (!adminAction) return
    setLoading(true)
    await onConfirm(adminComment, adminAction)
    setLoading(false)
    setAdminComment("")
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.ADMIN_ACTION_MODAL)
  }

  const handleCancel = () => {
    setAdminComment("")
    hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.ADMIN_ACTION_MODAL)
  }

  if (!adminAction) return null

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-3 rounded-full ${adminAction === "approve" ? "bg-green-100" : "bg-red-100"}`}>
            {adminAction === "approve" ? (
              <Check className="h-6 w-6 text-green-600" />
            ) : (
              <XCircle className="h-6 w-6 text-red-600" />
            )}
          </div>
          <h3 className="text-xl font-semibold text-slate-900">
            {adminAction === "approve" ? "Approve Rating" : "Reject Rating"}
          </h3>
        </div>

        <p className="text-slate-600 mb-4">Please provide a comment for this {adminAction} action:</p>

        <div className="mb-6">
          <label htmlFor="adminComment" className="block text-sm font-medium text-slate-700 mb-2">
            Admin Comment <span className="text-red-500">*</span>
          </label>
          <textarea
            id="adminComment"
            value={adminComment}
            onChange={(e) => setAdminComment(e.target.value)}
            placeholder={`Please explain why you are ${adminAction === "approve" ? "approving" : "rejecting"} this rating...`}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
            required
          />
        </div>

        <div className="flex gap-3 justify-end">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || !adminComment.trim()}
            className={`px-6 py-3 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center gap-2 ${
              adminAction === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {adminAction === "approve" ? <Check className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                {adminAction === "approve" ? "Approve" : "Reject"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
